import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '../services/authService'
import { apiService } from '../services/apiService'
import { SimpleSnackbar } from '../utils/SnackbarManager'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const userProfile = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const roles = ref([])
  const showProfileCompletionDialog = ref(false)

  // Getters
  const userInfo = computed(() => {
    return authService.getUserInfo()
  })

  // Computed property for reactive authentication state
  const isAuthenticated = computed(() => {
    return !!user.value
  })

  const initialize = async () => {
    loading.value = true
    try {
      const account = await authService.initialize()
      if (account) {
        user.value = account
        // Fetch user profile from API before loading roles
        try {
          await fetchUserProfile()
        } catch (e) {
          console.warn('Failed to fetch user profile during init:', e)
        }
        // Load roles from access token after user is set
        try {
          roles.value = await authService.getRoles('api')
        } catch (e) {
          console.warn('Failed to load roles during init:', e)
          roles.value = []
        }
      }
    } catch (error) {
      console.error('Auth initialization failed:', error)
      user.value = null
      userProfile.value = null
      roles.value = []
    } finally {
      loading.value = false
    }
  }

  const login = async () => {
    loading.value = true
    error.value = null

    try {
      const account = await authService.login()
      user.value = account
      // Fetch user profile from API before loading roles
      try {
        await fetchUserProfile()
      } catch (e) {
        console.warn('Failed to fetch user profile after login:', e)
      }
      // Refresh roles on login
      try {
        roles.value = await authService.getRoles('api')
      } catch (e) {
        console.warn('Failed to load roles after login:', e)
        roles.value = []
      }
      return account
    } catch (err) {
      error.value = err.message || 'Login failed'
      console.error('Login failed:', err)
      user.value = null
      userProfile.value = null
      roles.value = []
      throw err
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    error.value = null

    try {
      const didLogout = await authService.logout()
      if (didLogout) {
        user.value = null
        userProfile.value = null
        roles.value = []
      }
      return didLogout
    } catch (err) {
      error.value = err.message || 'Logout failed'
      console.error('Logout failed:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  const getAccessToken = async (scopeType = 'api') => {
    try {
      return await authService.getAccessToken(scopeType)
    } catch (err) {
      error.value = err.message || 'Failed to get access token'
      throw err
    }
  }

  const refreshToken = async () => {
    try {
      await authService.getAccessToken()
    } catch (error) {
      console.error('Token refresh failed:', error)
      logout()
      throw error
    }
  }

  const getApiToken = async () => {
    return getAccessToken('api')
  }

  const getGraphToken = async () => {
    return getAccessToken('graph')
  }

  const clearError = () => {
    error.value = null
  }

  const loadRoles = async () => {
    try {
      roles.value = await authService.getRoles('api')
      return roles.value
    } catch (e) {
      console.warn('Failed to load roles:', e)
      roles.value = []
      return roles.value
    }
  }

  const hasRole = (role) => {
    if (role == null) return false
    if (!Array.isArray(roles.value)) return false

    return roles.value.some(r => typeof r === 'string' && r.toLowerCase() === String(role).toLowerCase())
  }

  const fetchUserProfile = async () => {
    try {
      const profile = await apiService.get('/Users/me')
      userProfile.value = profile

      // Check if profile needs completion
      if (profile && needsProfileCompletion(profile)) {
        showProfileCompletionDialog.value = true
      }

      return profile
    } catch (err) {
      // Handle 404 - user not found, log them out
      if (err.statusCode === 404) {
        console.warn('User profile not found (404) - logging out user')
        userProfile.value = null
        // Show error message and logout
        SimpleSnackbar.error('User not found. Please sign in again.')
        await logout()
      } else {
        console.error('Failed to fetch user profile:', err.message || err)
        userProfile.value = null
      }
      return null
    }
  }

  const needsProfileCompletion = (profile) => {
    if (!profile) return false

    // Check if givenName, surname, or jobTitle is empty
    const givenNameEmpty = !profile.givenName || profile.givenName.trim() === ''
    const surnameEmpty = !profile.surname || profile.surname.trim() === ''
    const jobTitleEmpty = !profile.jobTitle || profile.jobTitle.trim() === ''

    return givenNameEmpty || surnameEmpty || jobTitleEmpty
  }

  const updateUserProfile = async (updates) => {
    try {
      if (!userProfile.value) {
        throw new Error('No user profile loaded')
      }

      // Only send the id and the fields being updated
      const payload = {
        id: userProfile.value.id,
        ...updates
      }

      const result = await apiService.patch('/Users/me', payload)
      userProfile.value = result
      showProfileCompletionDialog.value = false
      return result
    } catch (err) {
      console.error('Failed to update user profile:', err.message || err)
      throw err
    }
  }

  return {
    // State
    user,
    userProfile,
    loading,
    error,
    isAuthenticated,
    showProfileCompletionDialog,

    // Getters
    userInfo,

    // Actions
    initialize,
    login,
    logout,
    getAccessToken,
    refreshToken,
    getApiToken,
    getGraphToken,
    clearError,
    roles,
    loadRoles,
    hasRole,
    fetchUserProfile,
    updateUserProfile,
    needsProfileCompletion,
  }
})
